import { useCustomMutation } from '@refinedev/core';
import { KeyRound, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CopyButton } from '@/components/copy-button';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api-client';
import { generateTotpCode, totpSecondsRemaining } from '@/lib/totp';
import type { RevealCredentialsResponse } from '@/lib/types';

interface Props {
  accountId: string;
  accountEmail: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

type Revealed = RevealCredentialsResponse['data'];

// Edição de credenciais: revela automaticamente ao abrir (auditado como reveal
// normal) e mostra tudo EM CLARO — decisão do Lucas 16/08: o painel já é
// protegido por login e o fluxo de completar contas exige conferir os valores.
export function EditCredentialsDialog({ accountId, accountEmail, open, onOpenChange, onSaved }: Props) {
  const { mutate, isLoading: mutating } = useCustomMutation();

  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneProvider, setPhoneProvider] = useState('');

  const [totpCode, setTotpCode] = useState<string | null>(null);
  const [totpCountdown, setTotpCountdown] = useState(30);
  const [totpActive, setTotpActive] = useState(false);

  // Reveal automático na abertura
  useEffect(() => {
    if (!open) {
      setRevealed(null);
      setRevealError(null);
      setTotpActive(false);
      setTotpCode(null);
      return;
    }
    mutate(
      {
        url: `/psn-accounts/${accountId}/reveal`,
        method: 'post',
        values: { reason: `Edição de credenciais pelo painel (${accountEmail})` },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (result) => {
          const data = (result.data as unknown as RevealCredentialsResponse).data;
          setRevealed(data);
          setPassword(data.password ?? '');
          setTotpSecret(data.totp_secret ?? '');
          setPhone(data.phone ?? '');
          setPhoneProvider(data.phone_provider ?? '');
        },
        onError: (err) => {
          if (err instanceof ApiError && err.errorCode === 'rate_limited') {
            const retry = (err.body as { retry_after_sec?: number })?.retry_after_sec ?? 0;
            setRevealError(`Limite de reveals atingido. Tente em ~${Math.ceil(retry / 60)} min.`);
          } else {
            setRevealError('Falha ao carregar as credenciais atuais.');
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountId]);

  // Gerador TOTP (mesmo padrão da tela de criação)
  useEffect(() => {
    if (!totpActive || !totpSecret) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const code = await generateTotpCode(totpSecret);
        if (!cancelled) {
          setTotpCode(code);
          setTotpCountdown(totpSecondsRemaining());
        }
      } catch {
        if (!cancelled) {
          setTotpActive(false);
          setTotpCode(null);
        }
      }
    };
    const timer = window.setInterval(() => void refresh(), 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [totpSecret, totpActive]);

  const onGenerateTotp = async () => {
    try {
      const code = await generateTotpCode(totpSecret);
      setTotpCode(code);
      setTotpCountdown(totpSecondsRemaining());
      setTotpActive(true);
    } catch {
      setTotpActive(false);
      setTotpCode(null);
      toast.error('TOTP Secret Base32 inválido');
    }
  };

  const onSave = () => {
    if (!revealed) return;
    const changes: Record<string, string> = {};
    if (password && password !== (revealed.password ?? '')) changes.password = password;
    if (totpSecret && totpSecret.trim() !== (revealed.totp_secret ?? '')) changes.totp_secret = totpSecret.trim();
    if (phone && phone.trim() !== (revealed.phone ?? '')) changes.phone = phone.trim();
    if (phoneProvider && phoneProvider.trim() !== (revealed.phone_provider ?? '')) {
      changes.phone_provider = phoneProvider.trim();
    }
    if (Object.keys(changes).length === 0) {
      toast.info('Nada mudou.');
      return;
    }
    mutate(
      {
        url: `/psn-accounts/${accountId}/credentials`,
        method: 'put',
        values: changes,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          toast.success('Credenciais atualizadas.');
          onOpenChange(false);
          onSaved?.();
        },
        onError: () => toast.error('Falha ao salvar credenciais.'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar credenciais</DialogTitle>
          <DialogDescription>
            Valores atuais em claro. Abertura e alteração ficam no audit log. Campo que você não mexer não é alterado.
          </DialogDescription>
        </DialogHeader>

        {revealError ? (
          <p className="text-sm text-destructive">{revealError}</p>
        ) : !revealed ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando credenciais…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs uppercase text-muted-foreground">Email</Label>
              <div className="flex items-center gap-1 rounded-md border bg-muted/30 px-3 py-2">
                <span className="flex-1 truncate font-mono text-sm">{revealed.email}</span>
                <CopyButton value={revealed.email} label="Email" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cred-password">Senha</Label>
              <div className="flex items-center gap-1">
                <Input
                  id="cred-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono"
                />
                <CopyButton value={password} label="Senha" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cred-totp">TOTP Secret (Base32)</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="cred-totp"
                  value={totpSecret}
                  onChange={(e) => {
                    setTotpSecret(e.target.value);
                    setTotpActive(false);
                    setTotpCode(null);
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void onGenerateTotp()}
                  disabled={!totpSecret.trim()}
                >
                  <KeyRound />
                  Gerar código
                </Button>
              </div>
              {totpCode && (
                <div className="flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
                  <span className="font-mono text-2xl font-semibold tracking-[0.22em] text-emerald-400">
                    {totpCode}
                  </span>
                  <span className="text-xs text-muted-foreground">renova em {totpCountdown}s</span>
                </div>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cred-phone">Telefone</Label>
                <Input
                  id="cred-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cred-phone-provider">Provedor SMS</Label>
                <Input
                  id="cred-phone-provider"
                  placeholder="manual / 5sim / sms-activate"
                  value={phoneProvider}
                  onChange={(e) => setPhoneProvider(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={onSave} disabled={mutating}>
                {mutating && <Loader2 className="h-4 w-4 animate-spin" />}
                Salvar credenciais
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
